import React, { useState } from "react";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { MessageSquare, Bug, Lightbulb, AlertTriangle } from "lucide-react";
import { logError } from "@/lib/logError";

interface FeedbackDialogProps {
  trigger?: React.ReactNode;
  initialType?: 'feedback' | 'bug_report' | 'feature_request' | 'error_report';
  initialMessage?: string;
  errorDetails?: string;
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
}

export function FeedbackDialog({ 
  trigger, 
  initialType = 'feedback',
  initialMessage = '',
  errorDetails,
  open: externalOpen,
  onOpenChange: externalOnOpenChange
}: FeedbackDialogProps) {
  const [internalOpen, setInternalOpen] = useState(false);
  
  const open = externalOpen !== undefined ? externalOpen : internalOpen;
  const setOpen = externalOnOpenChange || setInternalOpen;
  const [type, setType] = useState(initialType);
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [message, setMessage] = useState(initialMessage);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { toast } = useToast();
  const { session } = useAuth();

  const feedbackTypes = [
    { id: 'feedback', label: 'General Feedback', icon: MessageSquare },
    { id: 'bug_report', label: 'Bug Report', icon: Bug },
    { id: 'feature_request', label: 'Feature Request', icon: Lightbulb },
    { id: 'error_report', label: 'Error Report', icon: AlertTriangle },
  ];

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!message.trim()) {
      toast({
        title: "Message required",
        description: "Please enter your feedback message.",
        variant: "destructive",
      });
      return;
    }

    setIsSubmitting(true);

    try {
      const feedbackData = {
        type,
        name: name.trim() || undefined,
        email: email.trim() || undefined,
        message: message.trim(),
        userAgent: navigator.userAgent,
        url: window.location.href,
        errorDetails,
        userId: session?.user?.id,
      };

      const { error } = await supabase.functions.invoke('send-feedback', {
        body: feedbackData,
      });

      if (error) throw error;

      toast({
        title: "Feedback sent!",
        description: "Thank you for your feedback. We'll review it soon.",
      });

      // Reset form
      setName('');
      setEmail('');
      setMessage('');
      setType('feedback');
      setOpen(false);

    } catch (error: any) {
      logError(error, { where: "Error sending feedback" });
      toast({
        title: "Failed to send feedback",
        description: "Please try again later or contact us directly.",
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const getDialogTitle = () => {
    switch (type) {
      case 'bug_report': return 'Report a Bug';
      case 'feature_request': return 'Request a Feature';
      case 'error_report': return 'Report an Error';
      default: return 'Send Feedback';
    }
  };

  const getDialogDescription = () => {
    switch (type) {
      case 'bug_report': return 'Help us fix issues by reporting bugs you encounter.';
      case 'feature_request': return 'Share your ideas for improving BRRGR.';
      case 'error_report': return 'Let us know about errors you\'ve experienced.';
      default: return 'We\'d love to hear your thoughts about BRRGR.';
    }
  };

  // In controlled mode (external open/onOpenChange), skip the trigger
  const isControlled = externalOpen !== undefined;

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      {!isControlled && (
        <DialogTrigger asChild>
          {trigger || (
            <Button variant="outline" size="sm">
              <MessageSquare className="w-4 h-4 mr-2" />
              Feedback
            </Button>
          )}
        </DialogTrigger>
      )}
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle>{getDialogTitle()}</DialogTitle>
          <DialogDescription>{getDialogDescription()}</DialogDescription>
        </DialogHeader>
        
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-3">
            <Label>Feedback Type</Label>
            <RadioGroup value={type} onValueChange={(value: string) => setType(value as typeof type)}>
              {feedbackTypes.map(({ id, label, icon: Icon }) => (
                <div key={id} className="flex items-center space-x-2">
                  <RadioGroupItem value={id} id={id} />
                  <Label htmlFor={id} className="flex items-center cursor-pointer">
                    <Icon className="w-4 h-4 mr-2" />
                    {label}
                  </Label>
                </div>
              ))}
            </RadioGroup>
          </div>

          {!session && (
            <>
              <div className="space-y-2">
                <Label htmlFor="name">Name (optional)</Label>
                <Input
                  id="name"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="Your name"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="email">Email (optional)</Label>
                <Input
                  id="email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="your.email@example.com"
                />
                <p className="text-xs text-muted-foreground">
                  We'll use this to follow up if needed.
                </p>
              </div>
            </>
          )}

          <div className="space-y-2">
            <Label htmlFor="message">Message *</Label>
            <Textarea
              id="message"
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              placeholder={
                type === 'bug_report'
                  ? 'Describe the bug and steps to reproduce it...'
                  : type === 'feature_request'
                  ? 'Describe the feature you\'d like to see...'
                  : 'Share your feedback...'
              }
              rows={4}
              required
            />
          </div>

          {errorDetails && (
            <div className="space-y-2">
              <Label>Error Details</Label>
              <div className="bg-muted p-3 rounded-md text-sm font-mono text-muted-foreground max-h-32 overflow-auto">
                {errorDetails}
              </div>
            </div>
          )}

          <div className="flex justify-end space-x-2 pt-4">
            <Button
              type="button"
              variant="outline"
              onClick={() => setOpen(false)}
              disabled={isSubmitting}
            >
              Cancel
            </Button>
            <Button type="submit" disabled={isSubmitting}>
              {isSubmitting ? 'Sending...' : 'Send Feedback'}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}